'use client'
import React, { useEffect, useState, useRef } from 'react'
import Carousel from 'react-multi-carousel'
import SliderSettings from './settings'
import Button from './../button'
import { useResponsive } from './../hooks'
import { WindowBreakpoints } from '@dustin-web/microsite-config'
import classNames from 'classnames'
import { CustomDot } from './carousel-dots'
import { SliderType } from './settings'

type SlideType = { slideIndex: number; amount: number }
type Props = {
  arrows?: boolean
  type: SliderType
  list: any

  dots?: boolean
  infinite?: boolean
  partialVisible?: boolean
  className?: string
  fullWidth?: boolean
  colorTheme?: string
  activeTab?: number
  autoPlay?: boolean
  draggable?: boolean
  shiftSlides?: SlideType[] | undefined

  autoPlaySpeed?: number
  dotMode?: 'full-width' | 'right'
  dotClass?: string
  itemClass?: string
  ssr?: boolean
}

export const CarouselTest = ({
  arrows = true,
  list = [],
  dots = true,
  type = 'default',
  infinite = false,
  partialVisible = false,
  className = '',
  fullWidth = true,
  colorTheme = 'gray',
  activeTab,
  autoPlay = false,
  draggable = true,
  shiftSlides = undefined,
  autoPlaySpeed = 5000,
  dotMode = 'full-width',
  dotClass = '',
  itemClass = '',
  ssr = true,
}: Props) => {
  const { breakpoints, isSmallScreen } = useResponsive()
  const [itemsToScroll, setItemsToScroll] = useState(1)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [smoothingClasses, setSmoothingClasses] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const carouselRef = useRef<Carousel | null>(null)
  const settings = SliderSettings()

  const getBreakpoint = (breakpoints: WindowBreakpoints) => {
    if (breakpoints.min.XL) {
      return 'xl'
    } else if (breakpoints.min.LG) {
      return 'lg'
    } else if (breakpoints.min.MD) {
      return 'md'
    } else if (breakpoints.min.SM) {
      return 'sm'
    } else {
      return 'mobile'
    }
  }

  const getItemsToScroll = (settings: any, breakpoints: any, type: string) => {
    if (breakpoints.min.XL) {
      return settings[`${type}`].xl.items
    } else if (breakpoints.min.LG) {
      return settings[`${type}`].lg.items
    } else if (breakpoints.min.MD) {
      return settings[`${type}`].md.items
    } else if (breakpoints.min.SM) {
      return settings[`${type}`].sm.items
    } else if (breakpoints.max.SM) {
      return settings[`${type}`].mobile.items
    } else {
      return 1
    }
  }

  useEffect(() => {
    setItemsToScroll(getItemsToScroll(settings, breakpoints, type))
  }, [breakpoints])

  useEffect(() => {
    carouselRef && carouselRef?.current?.goToSlide(0)
  }, [activeTab])

  // the SSR rendering cant know the width of the slider cards, this is applied on load and causes a layout shift.
  // So we need to render it like this.
  useEffect(() => {
    if (carouselRef?.current?.state.domLoaded === true) {
      return setSmoothingClasses(false)
    }

    removeAriaHiddenAttribute()
  }, [carouselRef.current?.state.domLoaded])

  // TODO: remove once https://github.com/YIZHUANG/react-multi-carousel/pull/442 is merged.
  const removeAriaHiddenAttribute = () => {
    const carouselItems = Array.from(document.getElementsByClassName('react-multi-carousel-item'))

    carouselItems?.forEach(element => element.removeAttribute('aria-hidden'))
  }

  const refocusOnFirstItemInSlide = (direction: 'left' | 'right') => {
    if (isSmallScreen) {
      return
    }

    const currentSlide = carouselRef.current?.state.currentSlide
    const carouselItemsElements = Array.from(
      carouselRef.current?.containerRef?.current?.getElementsByTagName('li') || []
    )

    if (typeof currentSlide === 'undefined' || !carouselItemsElements) {
      return
    }

    const refocusElement = (element: HTMLElement) => {
      element.setAttribute('tabindex', '-1')
      element.focus()
      element.removeAttribute('tabindex')
    }

    carouselItemsElements.forEach(element => {
      const elementDataIndex = element.getAttribute('data-index')

      if (!elementDataIndex) {
        return
      }

      if (
        parseInt(elementDataIndex) ===
        currentSlide + (direction === 'right' ? itemsToScroll : -itemsToScroll)
      ) {
        const refocus = () => refocusElement(element)
        // delay is requied here because the refocus needs to happen after slide change
        direction === 'right' ? setTimeout(refocus, 200) : refocus()
      }
    })
  }

  const LeftArrow = ({ onClick }: any) => (
    <Button
      className="c-carousel__arrow c-carousel__arrow-left c-imageslider__btn c-imageslider__btn--prev"
      icon="arrow-left-small"
      iconSize="i-32"
      onClick={() => {
        refocusOnFirstItemInSlide('left')
        onClick()
      }}
      aria-label="previous slide"
    />
  )

  const RightArrow = ({ onClick }: any) => (
    <Button
      className="c-carousel__arrow c-carousel__arrow-right c-imageslider__btn c-imageslider__btn--next"
      icon="arrow-right-small"
      iconSize="i-32"
      onClick={() => {
        refocusOnFirstItemInSlide('right')
        onClick()
      }}
      onKeyUp={event => {
        if (event.key === 'Enter') {
          refocusOnFirstItemInSlide('right')
        }
      }}
      aria-label="next slide"
    />
  )

  const slideShift = (slides: SlideType[]) => {
    if (slides?.length > 0) {
      return slides.find((slide: SlideType) => slide.slideIndex === currentSlideIndex)?.amount ?? 0
    }
    return undefined
  }

  return (
    <>
      <div
        onMouseEnter={() => setIsHovered(true)}
        className={classNames(
          `c-carousel-wrapper carousel-touch-action c-carousel--${type} ${fullWidth ? 'fw-true' : 'fw-false'} ${currentSlideIndex + itemsToScroll === list.length ? 'last-slide' : ''}`,
          {
            'ssr-smoothing': smoothingClasses,
          }
        )}>
        {!fullWidth && <div className={`slider-overlay slider-overlay--${colorTheme}`}></div>}
        <Carousel
          ref={carouselRef}
          ssr={ssr}
          partialVisible={partialVisible}
          infinite={infinite}
          slidesToSlide={itemsToScroll}
          transitionDuration={200}
          responsive={settings[`${type}`]}
          containerClass={className}
          renderButtonGroupOutside={false}
          minimumTouchDrag={1}
          arrows={arrows && breakpoints.min.LG}
          showDots={dots}
          dotListClass={dotMode}
          customDot={<CustomDot dotMode={dotMode} colorTheme={colorTheme} dotClass={dotClass} />}
          customTransition={'transform 300ms cubic-bezier(0.42, 0, 0.58, 1)'}
          draggable={draggable}
          autoPlay={autoPlay && !isHovered}
          autoPlaySpeed={autoPlaySpeed}
          afterChange={(__, { currentSlide }) => {
            setCurrentSlideIndex(currentSlide)
            removeAriaHiddenAttribute()
          }}
          additionalTransfrom={shiftSlides ? slideShift(shiftSlides) : undefined}
          deviceType={getBreakpoint(breakpoints)}
          // customDot={<CustomDot />}
          customLeftArrow={breakpoints.min.LG ? <LeftArrow /> : undefined}
          customRightArrow={breakpoints.min.LG ? <RightArrow /> : undefined}
          itemClass={itemClass}>
          {list}
        </Carousel>
      </div>
    </>
  )
}
